/*
 * Resetting a usage schedule's counter — the enum, the fold to one reading, and the two strings.
 *
 * Shaped like toolServiceStatus.js next door: the numeric enum first, then helpers that take an
 * `intl`. Labels come from cmmsMessages, never the other way round — cmmsMessages must not import
 * an enums module, or the cycle documented at the top of that file comes back as a TDZ error.
 *
 * Lives in features/cmms because two features reset a schedule: the schedules page, where the
 * planner works, and the CMMS tool register, where a tool at 98 % is what someone actually
 * notices. Neither owns the gesture.
 */
import cmmsMessages, { getServiceResetSourceLabel } from "./cmmsMessages";
import { formatDurationHours, formatQuantity, formatTimestamp } from "./cmmsFormat";

// Mirrors Models/Cmms/ServiceResetSource.cs. One-based like every CMMS enum, so an unset field is
// a wrong value rather than a silent WorkOrderClosed.
export const ServiceResetSource = {
  WorkOrderClosed: 1,
  Manual: 2,
  ThresholdSweep: 3,
};

// Mirrors Enums/ScheduleTriggerBasis. Repeated here rather than imported from
// cmmsRecurringSchedules/scheduleEnums for the reason toolServiceStatus.js repeats it: features
// /tools must not have to depend on a schedule feature to render a tool.
export const ResetTriggerBasis = {
  Calendar: 1,
  RunningTime: 2,
  Quantity: 3,
};

export { getServiceResetSourceLabel };

/*
 * One reading, whichever lane produced it.
 *
 * The two lanes answer "how far has this counter run" in two different shapes — a tool schedule
 * through /api/tool-monitoring/status, an asset one through /api/asset-consumption/schedule — and
 * neither is convertible into the other by the caller. Folding here is the same move
 * Models/Cmms/ScheduleReset.cs makes on the server, for the same reason: the button and the drawer
 * should not branch on the kind of schedule to draw one bar.
 *
 * Null when there is nothing to report. That is a real answer — a tool with no reading is a tool no
 * schedule watches — and returning a zeroed object instead would let a caller draw an empty bar
 * over a question nobody answered.
 */
export const normalizeScheduleReading = ({ consumption, toolStatus } = {}) => {
  if (toolStatus) {
    return {
      triggerBasis: toolStatus.triggerBasis,
      consumed: Number(toolStatus.consumed) || 0,
      // Non-nullable on this shape: a tool reading exists because a schedule set a threshold.
      interval: toolStatus.interval ?? null,
      remaining: toolStatus.remaining ?? null,
      percentConsumed: toolStatus.percentConsumed ?? null,
      // The server ranks the level; re-deriving it from the percentage here would disagree with
      // any schedule whose WarnAtPercent is not the default, and disagree in the reassuring
      // direction. ToolServiceStatusLevel.Overdue = 3.
      isOverdue: toolStatus.level === 3,
      // The tool lane's anchor is a fact — when the tool was last serviced. Carried so the
      // confirmation can say what it is counting from without reaching for IntervalStartingDate.
      lastServicedAt: toolStatus.lastServicedAt ?? null,
    };
  }

  if (consumption) {
    return {
      triggerBasis: consumption.triggerBasis,
      consumed: Number(consumption.consumed) || 0,
      // Nullable here, and legitimately so on a calendar schedule, which has no threshold.
      interval: consumption.interval ?? null,
      remaining: consumption.remaining ?? null,
      percentConsumed: consumption.percentConsumed ?? null,
      isOverdue: Boolean(consumption.isOverdue),
      // The asset lane has no service date to offer. IntervalStartingDate would fill this in and
      // must not: it is a measurement anchor the sweep and a basis change both move, and reading
      // it as "last reset" is the derivation BE-15.19 exists to remove.
      lastServicedAt: null,
    };
  }

  return null;
};

/*
 * A consumption figure with its unit attached.
 *
 * Hours as "412h 00m" rather than "412.4", matching formatDurationHours everywhere else in the
 * module; cycles as a whole number, because half a cycle is not a thing.
 */
const formatConsumedWithUnit = (reading, intl) => {
  if (reading.triggerBasis === ResetTriggerBasis.RunningTime) {
    return formatDurationHours(intl, reading.consumed);
  }
  return intl.formatMessage(cmmsMessages.resetCyclesValue, {
    cycles: formatQuantity(intl, reading.consumed, 0),
  });
};

/*
 * The reading itself: how far it has run against its threshold.
 *
 * Null when there is nothing to say, so a caller renders no line rather than a line about
 * nothing. A reading with no threshold still has a figure worth showing — it is a counter with
 * no target, not a counter with no value.
 */
export const buildScheduleReadingLabel = (reading, intl) => {
  if (!reading) return null;

  if (!reading.interval) return formatConsumedWithUnit(reading, intl);

  if (reading.triggerBasis === ResetTriggerBasis.RunningTime) {
    return intl.formatMessage(cmmsMessages.resetReadingHours, {
      consumed: formatDurationHours(intl, reading.consumed),
      interval: formatQuantity(intl, reading.interval, 0),
    });
  }

  return intl.formatMessage(cmmsMessages.resetReadingCycles, {
    consumed: formatQuantity(intl, reading.consumed, 0),
    interval: formatQuantity(intl, reading.interval, 0),
  });
};

/*
 * The percentage as a whole number, and whether it is past due.
 *
 * `percentConsumed` is clamped for the bar and left alone for the text — AssetUsageTab's rule,
 * kept because a bar cannot draw 140 % but a reader needs to be told it.
 */
export const getReadingProgress = (reading) => {
  const percent = reading?.percentConsumed;
  const value = percent != null ? Number(percent) : null;

  return {
    barValue: value != null ? Math.min(Math.max(value, 0), 100) : 0,
    label: value != null ? Math.round(value) : null,
    isOverdue: Boolean(reading?.isOverdue) || (value != null && value >= 100),
  };
};

/*
 * What a reset is about to discard, as one or two sentences.
 *
 * Returns null when there is no reading, and the caller then shows a figure-free confirmation.
 * That fallback is the point of the null: a dialog that says "0 h will stop counting" because a
 * fetch failed is worse than one that does not name a number at all.
 *
 * The second sentence says what the figure is counted *from*, and there are exactly two honest
 * sources for that date. `lastResetAt` is the newest entry in the schedule's own reset log, which
 * the drawer is already holding. `reading.lastServicedAt` is the tool lane's last service, which
 * is that lane's anchor — labelled as a service, because that is what it is. With neither, the
 * sentence names no date. IntervalStartingDate is on every schedule row and is not a third
 * option: it moves when the basis changes and when the sweep raises an order, and it says nothing
 * about who or when in reality.
 */
export const buildScheduleResetWarning = (reading, intl, { lastResetAt } = {}) => {
  if (!reading) {
    return intl.formatMessage(cmmsMessages.resetWarningNoReading);
  }

  const consumed = formatConsumedWithUnit(reading, intl);

  let lost;
  if (!reading.interval) {
    lost = intl.formatMessage(cmmsMessages.resetWarningNoInterval, { consumed });
  } else if (reading.triggerBasis === ResetTriggerBasis.RunningTime) {
    lost = intl.formatMessage(cmmsMessages.resetWarningHours, {
      consumed,
      interval: formatQuantity(intl, reading.interval, 0),
    });
  } else {
    lost = intl.formatMessage(cmmsMessages.resetWarningCycles, {
      consumed,
      interval: formatQuantity(intl, reading.interval, 0),
    });
  }

  let since;
  if (lastResetAt) {
    since = intl.formatMessage(cmmsMessages.resetSinceLastReset, {
      date: formatTimestamp(intl, lastResetAt),
    });
  } else if (reading.lastServicedAt) {
    since = intl.formatMessage(cmmsMessages.resetSinceLastService, {
      date: formatTimestamp(intl, reading.lastServicedAt),
    });
  } else {
    since = intl.formatMessage(cmmsMessages.resetSinceStart);
  }

  return `${lost} ${since}`;
};

/*
 * One line describing a reset that has already happened.
 *
 * The counterpart of buildToolServiceEntrySummary, and deliberately close to it: both units
 * always, notes excluded because they are free text and belong on their own line, and a user id
 * shown bare when the name cannot be resolved rather than blanked.
 *
 * It carries one thing that summary does not — the source. `resetByUserId` is null both for the
 * threshold sweep and for a token naming no user, which is exactly why BE-15.19 stored Source as
 * a column instead of working it out from what was missing. A line that showed neither would
 * present a sweep and a person's reset as the same event.
 */
export const buildScheduleResetEntrySummary = (entry, intl, resetByName) => {
  if (!entry) return "";

  const reading = intl.formatMessage(cmmsMessages.resetAtReading, {
    hours: formatQuantity(intl, entry.runningTimeHoursAtReset, 1),
    cycles: formatQuantity(intl, entry.quantityAtReset, 0),
  });

  const source = getServiceResetSourceLabel(entry.source, intl);

  // Omitted rather than dashed. A sweep has no user to name, and "Reset by —" reads as a field
  // that failed to load rather than as one that was never filled.
  const resetBy = entry.resetByUserId
    ? intl.formatMessage(cmmsMessages.resetBy, { user: resetByName || `#${entry.resetByUserId}` })
    : null;

  const workOrder = entry.cmmsWorkOrderId
    ? intl.formatMessage(cmmsMessages.toolHistoryWorkOrder, { id: entry.cmmsWorkOrderId })
    : null;

  return [reading, source, resetBy, workOrder].filter(Boolean).join(" · ");
};

/*
 * The newest reset in a log, or null.
 *
 * The endpoint answers newest first, so this is the first entry — not a re-sort. Re-ranking here
 * would invent a second ordering that could disagree with the list rendered underneath it.
 */
export const getLastResetAt = (entries) => entries?.[0]?.resetAt ?? null;

/*
 * Can this schedule's counter be reset at all?
 *
 * A calendar schedule has no counter — the server answers 400 — so the control is not rendered
 * rather than rendered and disabled. There is nothing to explain in a tooltip: the schedule does
 * not count anything.
 */
export const isResettableSchedule = (schedule) =>
  Boolean(schedule) && schedule.triggerBasis !== ResetTriggerBasis.Calendar;
